/**
 * Order Card - Reusable card for displaying order summary
 */

import { useTranslation } from '@ufc/core';
import { Badge, cn } from '@ufc/uikit';
import {
  Package,
  Truck,
  MapPin,
  Factory,
} from '@ufc/uikit';
import { getOrderStatusConfig, getProductColorStyles } from '../components/orders-section/constants';
import {
  getLocalizedDeliveryLocation,
  getLocalizedProductName,
  getLocalizedQuantityUnit,
} from '../components/orders-section/utils';
import type { Order } from '../../domain/types';

interface OrderCardProps {
  order: Order;
  onClick?: () => void;
  className?: string;
}

export function OrderCard({ order, onClick, className }: OrderCardProps) {
  const { t, i18n } = useTranslation();
  const config = getOrderStatusConfig(order.status);
  const StatusIcon = config.icon;
  const product = order.items[0];
  const productLabel = getLocalizedProductName(product?.product, i18n.language);
  const quantityUnit = getLocalizedQuantityUnit(
    product?.quantity.unit,
    t,
    i18n.language,
  );
  const deliveryLocation =
    getLocalizedDeliveryLocation(
      order.deliveryAddress,
      order.dropoffCity,
      i18n.language,
    ) ||
    t('common.notAvailable');

  return (
    <div
      className={cn(
        'p-4 rounded-xl border border-border/50 bg-white dark:bg-slate-800/50 transition-[box-shadow,border-color]',
        onClick &&
          'hover:bg-slate-50 dark:hover:bg-slate-800 cursor-pointer hover:border-primary/30 hover:shadow-md',
        className,
      )}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-bold text-foreground truncate mb-1">
            {order.orderNumber}
          </p>
          {(() => {
            const label = productLabel;
            const colors = getProductColorStyles(label || '');
            const isBulk = product?.quantity?.unit === 'tons';
            const PackageIcon = isBulk ? Truck : Package;

            return label ? (
              <div
                className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-[11px] font-semibold shadow-sm"
                style={{ backgroundColor: colors.bg, color: colors.text }}
              >
                <span
                  className="w-1.5 h-1.5 rounded-full ring-1 ring-white/50"
                  style={{ backgroundColor: colors.dot }}
                />
                <span className="max-w-[160px] truncate">{label}</span>
                <PackageIcon className="w-2.5 h-2.5 opacity-70" />
              </div>
            ) : null;
          })()}
        </div>
        <Badge
          className={cn(config.bg, config.text, 'border-0 gap-1 shrink-0 ms-2')}
        >
          <StatusIcon className="w-3 h-3" />
          {t(`orders.${order.status}`)}
        </Badge>
      </div>

      {/* Stats */}
      <div className="mb-3">
        <div className="p-2.5 rounded-lg bg-slate-50 dark:bg-slate-700/50">
          <div className="flex items-center gap-1.5 mb-0.5">
            <Package className="w-3 h-3 text-muted-foreground" />
            <span className="text-[10px] text-muted-foreground uppercase font-medium">
              {t('orders.quantity')}
            </span>
          </div>
          <p className="text-sm font-bold text-foreground">
            {product?.quantity.value} {quantityUnit}
          </p>
        </div>
      </div>

      {/* Dates */}
      <div className="grid grid-cols-2 gap-3 text-xs border-t border-border/30 pt-3">
        <div>
          <span className="text-muted-foreground">{t('orders.orderDate')}</span>
          <p className="font-medium text-foreground mt-0.5">
            {order.orderDate}
          </p>
        </div>
        <div>
          <span className="text-muted-foreground">
            {t('orders.deliveryDate')}
          </span>
          <p className="font-medium text-foreground mt-0.5">
            {order.deliveryDate}
          </p>
        </div>
      </div>

      {/* Address */}
      <div className="flex items-start gap-2 mt-3 pt-3 border-t border-border/30 text-xs">
        <MapPin className="w-3.5 h-3.5 text-muted-foreground mt-0.5 shrink-0" />
        <p className="text-muted-foreground line-clamp-1">
          {deliveryLocation}
        </p>
      </div>

      {/* Factory */}
      {order.factoryName && (
        <div className="flex items-center gap-2 mt-2 text-xs">
          <Factory className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
          <p className="text-muted-foreground truncate">{order.factoryName}</p>
        </div>
      )}
    </div>
  );
}
